import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import {
  AmazonElementMapping,
  MappingCompletionResult,
  MappingWorkflowIntent,
  ElementMappingView,
  MappingSuggestion,
  MappingSuggestionAlternative,
  AmazonFinanceValidationResult,
  TransactionDetail
} from '../../services/amazon-finance.models';
import { AmazonFinanceService } from '../../services/amazon-finance.service';

@Component({
  selector: 'gung-amazon-finance-mappings',
  templateUrl: './amazon-finance-mappings.component.html',
  styleUrl: './amazon-finance-mappings.component.scss',
  standalone: false
})
export class AmazonFinanceMappingsComponent implements OnInit {
  @Output() changed = new EventEmitter<void>();

  mappings: ElementMappingView[] = [];
  selected: AmazonElementMapping | null = null;
  selectedView: ElementMappingView | null = null;
  selectedOriginalId = '';
  editorMode: 'guided' | 'raw' = 'guided';
  validation: AmazonFinanceValidationResult | null = null;
  search = '';
  statusFilter = '';
  validationFilter = '';
  lineFilter = '';
  contextFilter = '';
  blockedOnly = false;
  deductingKeys = '';
  loading = false;
  saving = false;
  error = '';
  saved = false;
  suggesting = false;
  suggestion: MappingSuggestion | null = null;
  suggestionApplied = false;
  suggestionError = '';
  completing = false;
  deleting = false;
  completionResult: MappingCompletionResult | null = null;
  evidenceTransactionIds: string[] = [];
  inspectedTransaction: TransactionDetail | null = null;
  transactionInspectionLoading = false;
  transactionInspectionError = '';
  private transactionInspectionRequestId = '';

  constructor(
    private readonly financeService: AmazonFinanceService,
    private readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  get filteredMappings(): ElementMappingView[] {
    const query = this.search.trim().toLowerCase();
    return this.mappings.filter(view => {
      const mapping = view.mapping;
      const matchesQuery =
        !query ||
        [
          mapping.id,
          mapping.functionalDescription,
          mapping.descriptionPrefix,
          mapping.productIdOverride,
          mapping.accountNumberOverride?.toString(),
          mapping.taxElementKey,
          mapping.taxCancellingElementKey
        ].some(value => value?.toLowerCase().includes(query));
      const matchesStatus = !this.statusFilter || mapping.status === this.statusFilter;
      const matchesValidation =
        !this.validationFilter || (this.validationFilter === 'valid' ? view.validation.valid : !view.validation.valid);
      const matchesLine =
        !this.lineFilter || (this.lineFilter === 'creates' ? mapping.shouldCreateLine : !mapping.shouldCreateLine);
      const matchesContext =
        !this.contextFilter || (this.contextFilter === 'product' ? mapping.productContext : !mapping.productContext);
      const matchesBlocked = !this.blockedOnly || view.blockedTransactions > 0;
      return matchesQuery && matchesStatus && matchesValidation && matchesLine && matchesContext && matchesBlocked;
    });
  }

  get mappingStatuses(): string[] {
    return Array.from(
      new Set(this.mappings.map(view => view.mapping.status).filter((value): value is string => !!value))
    ).sort();
  }

  load(selectId?: string, preservedEvidenceTransactionIds: string[] = []): void {
    this.loading = true;
    this.error = '';
    this.financeService
      .getMappings()
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: mappings => {
          this.mappings = mappings;
          if (selectId) {
            const selected = mappings.find(view => view.mapping.id === selectId);
            if (selected) {
              this.select(selected);
              this.addEvidenceTransactionIds(preservedEvidenceTransactionIds);
            }
          }
        },
        error: () => (this.error = 'AMAZON_FINANCE_MAPPINGS_LOAD_ERROR')
      });
  }

  select(view: ElementMappingView): void {
    this.selectedView = view;
    this.selected = this.copy(view.mapping);
    this.selectedOriginalId = view.mapping.id;
    this.validation = view.validation;
    this.deductingKeys = (view.mapping.amountDeductingElementKeys || []).join('\n');
    this.saved = false;
    this.completionResult = null;
    this.editorMode = 'guided';
    this.evidenceTransactionIds = this.discoveryTransactionIds(view.mapping);
    this.closeTransactionInspector();
    this.resetSuggestion();
  }

  create(): void {
    this.selected = {
      id: '',
      shouldCreateLine: false,
      productContext: false,
      taxElementKey: null,
      taxCancellingElementKey: null,
      suffix: null,
      productIdOverride: null,
      priority: null,
      accountNumberOverride: null,
      accountNumberOverrideByCustomerId: {},
      accountNumberOverrideByDeliveryCountry: {},
      taxPercentOverride: null,
      descriptionPrefix: null,
      impliedVAT: false,
      amountDeductingElementKeys: [],
      functionalDescription: null,
      workflowIntent: null,
      status: 'INCOMPLETE',
      amazonBuying: false
    };
    this.selectedOriginalId = '';
    this.selectedView = null;
    this.validation = null;
    this.deductingKeys = '';
    this.saved = false;
    this.completionResult = null;
    this.editorMode = 'guided';
    this.evidenceTransactionIds = [];
    this.closeTransactionInspector();
    this.resetSuggestion();
  }

  selectAndSuggest(view: ElementMappingView): void {
    this.select(view);
    this.suggestMapping();
  }

  suggestMapping(): void {
    if (!this.selectedOriginalId || this.suggesting) return;
    this.suggesting = true;
    this.suggestionError = '';
    this.suggestionApplied = false;
    const language = this.translate.currentLang || this.translate.defaultLang || 'en';
    this.financeService
      .suggestMapping(this.selectedOriginalId, language)
      .pipe(finalize(() => (this.suggesting = false)))
      .subscribe({
        next: suggestion => {
          this.suggestion = suggestion;
          this.addEvidenceTransactionIds(suggestion.evidenceTransactionIds);
        },
        error: error =>
          (this.suggestionError =
            error?.error?.detail || error?.error?.message || 'AMAZON_FINANCE_MAPPING_SUGGESTION_ERROR')
      });
  }

  applySuggestion(mapping = this.suggestion?.proposedMapping, validation = this.suggestion?.validation ?? null): void {
    if (!mapping) return;
    this.selected = this.copy(mapping);
    this.selected.id = this.selectedOriginalId;
    this.selected.workflowIntent = this.selected.workflowIntent || this.inferWorkflowIntent(this.selected);
    this.addEvidenceTransactionIds(this.suggestion?.evidenceTransactionIds || []);
    this.deductingKeys = (this.selected.amountDeductingElementKeys || []).join('\n');
    this.validation = validation;
    this.suggestion = null;
    this.suggestionApplied = true;
    this.saved = false;
    this.completionResult = null;
  }

  applyAlternative(alternative: MappingSuggestionAlternative): void {
    this.applySuggestion(alternative.proposedMapping ?? undefined, null);
  }

  dismissSuggestion(): void {
    const suggestion = this.suggestion;
    this.suggestion = null;
    if (suggestion) {
      this.financeService.rejectMappingSuggestion(suggestion.mappingId, suggestion.suggestionId).subscribe({
        error: () => undefined
      });
    }
  }

  suggestionChanges(): { field: string; before: string; after: string }[] {
    if (!this.selected || !this.suggestion) return [];
    const fields: (keyof AmazonElementMapping)[] = [
      'functionalDescription',
      'workflowIntent',
      'shouldCreateLine',
      'productContext',
      'amazonBuying',
      'impliedVAT',
      'productIdOverride',
      'accountNumberOverride',
      'taxPercentOverride',
      'taxElementKey',
      'taxCancellingElementKey',
      'amountDeductingElementKeys',
      'descriptionPrefix',
      'suffix',
      'priority',
      'status'
    ];
    return fields
      .map(field => ({
        field: this.fieldLabel(field),
        before: this.displayValue(this.selected?.[field]),
        after: this.displayValue(this.suggestion?.proposedMapping[field])
      }))
      .filter(change => change.before !== change.after);
  }

  validate(): void {
    if (!this.selected) return;
    this.prepareMapping();
    this.financeService.validateMapping(this.selected).subscribe({
      next: result => (this.validation = result),
      error: () => (this.error = 'AMAZON_FINANCE_VALIDATION_ERROR')
    });
  }

  save(): void {
    if (!this.selected || !this.selected.id.trim()) return;
    this.prepareMapping();
    this.saving = true;
    this.error = '';
    this.saved = false;
    this.financeService
      .saveMapping(this.selected)
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: result => {
          const evidenceTransactionIds = [...this.evidenceTransactionIds];
          this.validation = result.validation;
          this.saved = true;
          this.changed.emit();
          this.load(result.mapping.id, evidenceTransactionIds);
        },
        error: error => (this.error = error?.error?.message || 'AMAZON_FINANCE_SAVE_ERROR')
      });
  }

  saveDraft(): void {
    if (!this.selected) return;
    this.selected.status = 'INCOMPLETE';
    this.save();
  }

  completeAndQueue(): void {
    if (!this.selected || !this.selected.id.trim() || !this.selected.workflowIntent || this.completing) return;
    this.prepareMapping();
    this.selected.status = 'COMPLETE';
    this.completing = true;
    this.error = '';
    this.saved = false;
    this.completionResult = null;
    this.financeService
      .completeMapping(this.selected)
      .pipe(finalize(() => (this.completing = false)))
      .subscribe({
        next: result => {
          this.completionResult = result;
          this.selectedView = {
            ...result.mapping,
            blockedTransactions: result.remainingBlockedTransactions
          };
          this.selected = this.copy(result.mapping.mapping);
          this.selectedOriginalId = result.mapping.mapping.id;
          this.validation = result.mapping.validation;
          this.saved = true;
          const index = this.mappings.findIndex(view => view.mapping.id === result.mapping.mapping.id);
          if (index >= 0) {
            this.mappings[index] = this.selectedView;
            this.mappings = [...this.mappings];
          }
          this.changed.emit();
        },
        error: error => (this.error = error?.error?.message || 'AMAZON_FINANCE_MAPPING_COMPLETION_ERROR')
      });
  }

  setWorkflowIntent(intent: MappingWorkflowIntent): void {
    if (!this.selected) return;
    this.selected.workflowIntent = intent;
    if (intent === 'CREATE_LINE') {
      this.selected.shouldCreateLine = true;
    } else if (intent === 'REFERENCE_ONLY' || intent === 'IGNORE') {
      this.selected.shouldCreateLine = false;
    }
    if (intent === 'IGNORE') {
      this.selected.taxElementKey = null;
      this.selected.taxCancellingElementKey = null;
      this.selected.amountDeductingElementKeys = [];
      this.deductingKeys = '';
    }
    this.selected.status = 'INCOMPLETE';
    this.validation = null;
    this.saved = false;
    this.completionResult = null;
  }

  workflowIntent(): MappingWorkflowIntent | null {
    return this.selected?.workflowIntent || null;
  }

  canDeleteOrphanPlaceholder(): boolean {
    return !!this.selectedOriginalId
      && !this.deleting
      && !this.completing
      && !this.saving
      && !this.suggesting
      && this.selected?.status?.toUpperCase() !== 'COMPLETE'
      && this.selectedView?.blockedTransactions === 0;
  }

  deleteOrphanPlaceholder(): void {
    if (!this.canDeleteOrphanPlaceholder()) return;
    const mappingId = this.selectedOriginalId;
    if (!window.confirm(this.translate.instant('REMOVE_ORPHAN_MAPPING_CONFIRM', { mappingId }))) return;
    this.deleting = true;
    this.error = '';
    this.financeService
      .deleteOrphanMapping(mappingId)
      .pipe(finalize(() => (this.deleting = false)))
      .subscribe({
        next: () => {
          this.mappings = this.mappings.filter(view => view.mapping.id !== mappingId);
          this.closeEditor();
          this.changed.emit();
        },
        error: error =>
          (this.error = error?.error?.detail || error?.error?.message || 'REMOVE_ORPHAN_MAPPING_ERROR')
      });
  }

  inspectTransaction(transactionId: string): void {
    if (!transactionId || this.transactionInspectionLoading) return;
    this.transactionInspectionRequestId = transactionId;
    this.transactionInspectionLoading = true;
    this.transactionInspectionError = '';
    this.inspectedTransaction = null;
    this.financeService
      .getTransaction(transactionId)
      .pipe(
        finalize(() => {
          if (this.transactionInspectionRequestId === transactionId) this.transactionInspectionLoading = false;
        })
      )
      .subscribe({
        next: detail => {
          if (this.transactionInspectionRequestId === transactionId) this.inspectedTransaction = detail;
        },
        error: () => {
          if (this.transactionInspectionRequestId === transactionId) {
            this.transactionInspectionError = 'SOURCE_TRANSACTION_LOAD_ERROR';
          }
        }
      });
  }

  closeTransactionInspector(): void {
    this.transactionInspectionRequestId = '';
    this.transactionInspectionLoading = false;
    this.inspectedTransaction = null;
    this.transactionInspectionError = '';
  }

  closeEditor(): void {
    this.selected = null;
    this.selectedView = null;
    this.selectedOriginalId = '';
    this.completionResult = null;
    this.evidenceTransactionIds = [];
    this.closeTransactionInspector();
    this.resetSuggestion();
  }

  resetFilters(): void {
    this.search = '';
    this.statusFilter = '';
    this.validationFilter = '';
    this.lineFilter = '';
    this.contextFilter = '';
    this.blockedOnly = false;
  }

  private prepareMapping(): void {
    if (!this.selected) return;
    this.selected.id = this.selected.id.trim();
    this.selected.amountDeductingElementKeys = this.deductingKeys
      .split(/[\n,]/)
      .map(value => value.trim())
      .filter(Boolean);
  }

  private copy(mapping: AmazonElementMapping): AmazonElementMapping {
    return JSON.parse(JSON.stringify(mapping));
  }

  private inferWorkflowIntent(mapping: AmazonElementMapping): MappingWorkflowIntent {
    if (mapping.shouldCreateLine) return 'CREATE_LINE';
    if (mapping.taxElementKey || mapping.taxCancellingElementKey || mapping.amountDeductingElementKeys?.length) {
      return 'REFERENCE_ONLY';
    }
    return mapping.status === 'COMPLETE' ? 'IGNORE' : 'CUSTOM';
  }

  private discoveryTransactionIds(mapping: AmazonElementMapping): string[] {
    const source = mapping.mappingCreationSource?.trim();
    if (!source || source === 'AMAZON_FINANCE_UI' || source.startsWith('AI_SUGGESTION:')) return [];
    return [source];
  }

  private addEvidenceTransactionIds(transactionIds: string[]): void {
    this.evidenceTransactionIds = Array.from(
      new Set([...this.evidenceTransactionIds, ...transactionIds.map(id => id.trim()).filter(id => !!id)])
    );
  }

  private resetSuggestion(): void {
    this.suggestion = null;
    this.suggestionApplied = false;
    this.suggestionError = '';
  }

  private displayValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private fieldLabel(field: keyof AmazonElementMapping): string {
    const labels: Partial<Record<keyof AmazonElementMapping, string>> = {
      functionalDescription: 'FUNCTIONAL_DESCRIPTION',
      workflowIntent: 'MAPPING_PURPOSE',
      shouldCreateLine: 'CREATE_FINANCE_LINE',
      productContext: 'PRODUCT_CONTEXT',
      amazonBuying: 'AMAZON_BUYING_SIDE',
      impliedVAT: 'IMPLIED_VAT',
      productIdOverride: 'PRODUCT_ID_OVERRIDE',
      accountNumberOverride: 'ACCOUNT_NUMBER_OVERRIDE',
      taxPercentOverride: 'TAX_PERCENT_OVERRIDE',
      taxElementKey: 'TAX_ELEMENT_KEY',
      taxCancellingElementKey: 'TAX_CANCELLING_KEY',
      amountDeductingElementKeys: 'AMOUNT_DEDUCTING_KEYS',
      descriptionPrefix: 'DESCRIPTION_PREFIX',
      suffix: 'SUFFIX',
      priority: 'PRIORITY',
      status: 'STATUS'
    };
    return this.translate.instant(labels[field] ?? String(field));
  }
}
